"use client";

import { useCallback, useState, memo } from "react";
import { LayoutGrid, List, Sparkles, Check, ListVideo } from "lucide-react";
import { toast } from "sonner";

import { trpc } from "@/lib/trpc/client";

const PRESETS = [
  {
    id: "grid" as const,
    name: "Grid",
    description: "Uniform tiles in a responsive grid",
    icon: LayoutGrid,
  },
  {
    id: "list" as const,
    name: "List",
    description: "Horizontal rows with metadata",
    icon: List,
  },
  {
    id: "hero-grid" as const,
    name: "Hero + Grid",
    description: "Featured item with grid below",
    icon: Sparkles,
  },
  {
    id: "playlist" as const,
    name: "Playlist",
    description: "Video player with playlist",
    icon: ListVideo,
  },
] as const;

type PresetId = (typeof PRESETS)[number]["id"];

interface LayoutPickerProps {
  collectionId: string;
  initialPreset: string;
  initialConfig: Record<string, unknown> | null;
}

/** Mini wireframe preview for each layout preset */
function PresetPreview({
  presetId,
}: {
  presetId: PresetId;
}): React.ReactElement {
  switch (presetId) {
    case "grid":
      return (
        <div className="grid grid-cols-3 gap-1">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="aspect-video rounded-sm bg-muted-foreground/20"
            />
          ))}
        </div>
      );
    case "list":
      return (
        <div className="flex flex-col gap-1">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-1">
              <div className="h-3 w-5 shrink-0 rounded-sm bg-muted-foreground/20" />
              <div className="h-1.5 flex-1 rounded-full bg-muted-foreground/15" />
            </div>
          ))}
        </div>
      );
    case "hero-grid":
      return (
        <div className="flex flex-col gap-1">
          <div className="aspect-[21/9] w-full rounded-sm bg-muted-foreground/25" />
          <div className="grid grid-cols-3 gap-1">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="aspect-video rounded-sm bg-muted-foreground/20"
              />
            ))}
          </div>
        </div>
      );
    case "playlist":
      return (
        <div className="flex gap-1">
          <div className="aspect-video flex-1 rounded-sm bg-muted-foreground/25" />
          <div className="flex w-1/3 flex-col gap-1">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-3 rounded-sm bg-muted-foreground/20" />
            ))}
          </div>
        </div>
      );
  }
}

function LayoutPicker({
  collectionId,
  initialPreset,
  initialConfig,
}: LayoutPickerProps): React.ReactElement {
  const [selectedPreset, setSelectedPreset] = useState<string>(
    initialPreset || "grid",
  );
  const [isSaving, setIsSaving] = useState(false);

  const updateLayout = trpc.collections.updateLayout.useMutation({
    onSuccess: () => {
      toast.success("Layout updated");
      setIsSaving(false);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update layout");
      setIsSaving(false);
    },
  });

  const handleSelect = useCallback(
    (presetId: string): void => {
      setSelectedPreset(presetId);
      setIsSaving(true);

      updateLayout.mutate({
        collectionId,
        layoutPreset: presetId as any,
        layoutConfig: null,
      });
    },
    [collectionId, updateLayout],
  );

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-medium text-foreground">Layout</h3>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {PRESETS.map((preset) => {
          const isSelected = selectedPreset === preset.id;
          const Icon = preset.icon;

          return (
            <button
              key={preset.id}
              onClick={() => handleSelect(preset.id)}
              disabled={isSaving}
              className={`relative flex flex-col gap-2 rounded-lg border-2 p-3 text-left transition-colors ${
                isSelected
                  ? "border-primary bg-primary/5"
                  : "border-border bg-card hover:border-primary/50"
              }`}
            >
              {/* Selected indicator */}
              {isSelected && (
                <div className="absolute right-2 top-2 rounded-full bg-primary p-0.5">
                  <Check className="h-3 w-3 text-primary-foreground" />
                </div>
              )}

              {/* Preview wireframe */}
              <div className="w-full rounded border border-border bg-background p-2">
                <PresetPreview presetId={preset.id} />
              </div>

              {/* Label */}
              <div className="flex items-center gap-1.5">
                <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs font-medium text-foreground">
                  {preset.name}
                </span>
              </div>

              <p className="text-[10px] leading-tight text-muted-foreground">
                {preset.description}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default memo(LayoutPicker);
